import { of , Subject } from 'rxjs';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ItemService } from '../services';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { map, takeUntil, tap } from 'rxjs/operators';
import { ItemContextModel } from '../model';

@Component({
    selector: 'bookmarked-pane',
    styles: [`
        mat-card {
            margin-bottom: 6px;
        }
        
        .title {
            font-size: 20px;
        }

        .no-results {
            height: 100%
        }

        .title-item {
            display: inline-block;
        }
        
        .itemsPanel {
            overflow-y: auto;
            height: calc(100vh - 130px);
        }
    `],
    template: `
        <div *ngIf="noTouching && !showItem" fxLayout="column" fxLayoutAlign="center center" style="height:100%">
            <spinner></spinner>
        </div>
        <div fxLayout="column" *ngIf="!noTouching && !showItem">
            <mat-card>
                <mat-card-content>
                    <mat-card-title class="title">
                        <div class="title-item">Bookmarked Items</div>
                        <mat-chip-list *ngIf="totalItems" class="title-item" style="padding-left: 20px;">
                            <mat-chip>{{totalItems}}</mat-chip>
                        </mat-chip-list>
                        <button mat-button 
                            [matTooltip]="'Sorted by ' + (searchParams.sortField === 'publishedDate' ? 'Published Date' : 'Attention Rank')"
                            color="primary" 
                            [matMenuTriggerFor]="menu"><mat-icon>filter_list</mat-icon></button>
                        <mat-menu #menu="matMenu">
                            <button mat-menu-item 
                                    matTooltip="by Published Date"
                                    [disabled]="searchParams.sortField === 'publishedDate'"
                                    (click)="sort('publishedDate')"><mat-icon>schedule</mat-icon>Time</button>
                            <button mat-menu-item
                                    matTooltip="by Attention Rank"
                                    [disabled]="searchParams.sortField === 'attentionRank'" 
                                    (click)="sort('attentionRank')"><mat-icon>photo_filter</mat-icon>Rank</button>
                        </mat-menu>
                    </mat-card-title>
                </mat-card-content>
            </mat-card>
            <div *ngIf="!noTouching && !items.length" fxLayout="column" fxLayoutAlign="center center" class="no-results">
                <h1 class="text-center"><i class="far fa-bookmark"></i></h1>
                <div>No bookmarked items.</div>
            </div>
            <div class="itemsPanel"
                 infiniteScroll 
                 [infiniteScrollThrottle]="300"
                 [infiniteScrollDistance]="1"
                 [scrollWindow]="false"
                 (scrolled)="nextPage()">
                <span *ngFor="let item of items">
                    <item-summary [item]="item"></item-summary>
                </span>
            </div>
        </div>
    `})
export class BookmarkedPane implements OnDestroy, OnInit {
    cleanupTime = new Subject<void>();
    items: any = [];
    totalItems: number;
    showItem: boolean = false;
    noTouching: boolean = true;
    request: {page: number, includeFullDescription: boolean, sortField: string, includeReadItems?: boolean, createdAfterDateTime?: boolean};

    searchParams = {
        page: -1,
        sortField: 'publishedDate'
    };

    constructor(private router: Router, private route: ActivatedRoute, private itemService: ItemService){}

    ngOnInit(){
        this.router.events.pipe(takeUntil(this.cleanupTime)).subscribe(() => this.showItem = !!this.route.firstChild );
        this.fetchItems();
        
        this.route.url.pipe(takeUntil(this.cleanupTime)).subscribe((segs: UrlSegment[]) => {
            if (segs.length) {
                this.itemService.focusedItemEdit.next(undefined);
                const itemId = segs[1].path;
                let item = this.items.find(item => item.id === itemId);
                const isSource = false; 
                const curating = false;
                if (!item) {
                    this.itemService.getItem(itemId)
                    .pipe(map(response => response.item))
                    .subscribe(fetchedItem => this.itemService.focusedItem.next(new ItemContextModel(fetchedItem, undefined, undefined, undefined, isSource, curating)));
                } else {
                    this.itemService.focusedItem.next(new ItemContextModel(item, undefined, undefined, undefined, isSource, curating));
                }
            } else {
                this.itemService.focusedItem.next(undefined);
            }
        });
        this.itemService.itemClosing.pipe(takeUntil(this.cleanupTime)).subscribe(() => this.router.navigateByUrl(this.itemService.childClearedPath()));
        this.itemService.itemContextChanged.pipe(takeUntil(this.cleanupTime)).subscribe(() => this.fetchItems());
    }

    sort(sortField): void {
        this.searchParams.sortField = sortField;
        this.searchParams.page = -1;
        this.noTouching = true;
        this.items = [];
        this.fetchItems();
    }

    nextPage = () => this.retrievePage().subscribe(() => this.noTouching = false);

    retrievePage = () => {
        this.searchParams.page++;
        return this.itemService.getBookmarkedItems({ page: this.searchParams.page, includeFullDescription: true, sortField: this.searchParams.sortField })
        .pipe(
            tap((itemResults: any) => {
                itemResults.content.forEach(item =>{
                    item.visible = true;
                    this.items.push(item);
                });
        }));
    }

    fetchItems = () => {
        this.noTouching = true;
        this.retrievePage()
        .subscribe(itemResults => {
            this.noTouching = false;
            this.totalItems = itemResults.totalElements;
        });
    };

    ngOnDestroy() {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    };
}
